import { meteredAudio } from "./voiceMeter";

// Records the 16:9 video stage (source video + voice bars) from a shared
// browser tab, with the tab's audio (source sound + the AI voice) onto a 1920x1080 canvas.
export class TabRecorder {
  constructor({ sourceEl }) {
    this.sourceEl = sourceEl;
    this.chunks = [];
    this.running = false;
  }

  async prepare() {
    this.tabStream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 30 },
      audio: true,
      preferCurrentTab: true,
      selfBrowserSurface: "include",
      systemAudio: "include",
    });
    this.hasTabAudio = this.tabStream.getAudioTracks().length > 0;
    this.tabVideo = document.createElement("video");
    this.tabVideo.srcObject = this.tabStream;
    this.tabVideo.muted = true;
    await this.tabVideo.play();
    this.canvas = document.createElement("canvas");
    this.canvas.width = 1920;
    this.canvas.height = 1080;
    this.ctx = this.canvas.getContext("2d");
    this.running = true;
    this._draw();
    this.tabStream.getVideoTracks()[0].onended = () => this.onTabEnded && this.onTabEnded();
    return this.hasTabAudio;
  }

  begin() {
    const mixed = new MediaStream([...this.canvas.captureStream(30).getVideoTracks(), ...this.tabStream.getAudioTracks()]);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
    this.recorder = new MediaRecorder(mixed, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
    this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.startTime = performance.now();
    this.recorder.start(1000);
  }

  _cropForSource() {
    const s = this.tabStream.getVideoTracks()[0].getSettings();
    const rect = this.sourceEl.getBoundingClientRect();
    const sx = (s.width || window.innerWidth) / window.innerWidth;
    const sy = (s.height || window.innerHeight) / window.innerHeight;
    return { x: rect.left * sx, y: rect.top * sy, w: rect.width * sx, h: rect.height * sy };
  }

  _draw = () => {
    if (!this.running) return;
    this.ctx.fillStyle = "#000";
    this.ctx.fillRect(0, 0, 1920, 1080);
    try {
      const c = this._cropForSource();
      this.ctx.drawImage(this.tabVideo, c.x, c.y, c.w, c.h, 0, 0, 1920, 1080);
    } catch (e) { /* frame not ready */ }
    this.raf = requestAnimationFrame(this._draw);
  };

  // Release the tab share without producing a file (e.g. cancelled before recording began).
  abort() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    this.tabStream?.getTracks().forEach((t) => t.stop());
  }

  async stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    const blob = await new Promise((resolve) => {
      this.recorder.onstop = () => resolve(new Blob(this.chunks, { type: "video/webm" }));
      this.recorder.stop();
    });
    const duration = (performance.now() - this.startTime) / 1000;
    this.tabStream.getTracks().forEach((t) => t.stop());
    return { blob, duration };
  }
}

// Plays the source from the start, pausing at each comment while its voice clip plays.
// Resolves when the source has ended and every comment has spoken, or when aborted.
export function playWithComments({ player, comments, urls, onSpeak, signal }) {
  return new Promise((resolve) => {
    let idx = 0, busy = false, done = false, started = false, audio = null;
    const finish = () => {
      if (done) return;
      done = true;
      clearInterval(iv);
      try { audio?.pause(); } catch (e) { /* noop */ }
      onSpeak(null);
      resolve();
    };
    signal.addEventListener("abort", finish);
    const speakOne = () => new Promise((res) => {
      const i = idx++;
      onSpeak(comments[i].id);
      audio = meteredAudio(urls[i]);
      const end = () => { onSpeak(null); res(); };
      audio.onended = end;
      audio.onerror = end;
      audio.play().catch(end);
    });
    const speakDue = async (t, all) => {
      busy = true;
      while (!done && idx < comments.length && (all || comments[idx].t <= t + 0.15)) await speakOne();
      busy = false;
    };
    const iv = setInterval(async () => {
      if (done || busy) return;
      const state = player.getPlayerState();
      if (state === 1) started = true;
      if (!started) return;
      if (state === 0) { await speakDue(0, true); finish(); return; }
      const t = player.getCurrentTime();
      if (idx < comments.length && t >= comments[idx].t - 0.1) {
        player.pauseVideo();
        await speakDue(t, false);
        if (!done) player.playVideo();
      }
    }, 100);
  });
}

let ytApiPromise = null;
export function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    });
  }
  return ytApiPromise;
}
